import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { HardHat, ArrowLeft, ShieldCheck, Loader2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ResetPassword — completes the reset flow. Reads the one-time token from
 * `?token=…` and swaps it for a new password server-side. Strength gate
 * mirrors the backend rule (≥8 chars + ≥1 digit) so the button doesn't lie
 * about what will succeed. On success we toast + redirect to /login rather
 * than auto-logging-in — a returning tradie who just clicked a link
 * probably shares the device and would rather confirm they can sign in
 * with the new password than land in the app one step later.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const passwordProblem = (() => {
    if (!password) return "";
    if (password.length < 8) return "Must be at least 8 characters.";
    if (!/[0-9]/.test(password)) return "Must include at least one number.";
    if (confirm && password !== confirm) return "Passwords don't match.";
    return "";
  })();
  const canSubmit = !!token && !passwordProblem && password && confirm && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError("This reset link is missing or malformed.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      toast.success("Password updated. Please sign in.");
      nav("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "We couldn't reset your password. The link may have expired."
      );
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1F1F1F] bi-grain px-4">
      <div className="w-full max-w-md relative z-10" data-testid="reset-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-[#FF9500] flex items-center justify-center text-[#111827]">
            <HardHat className="w-6 h-6" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-white text-2xl tracking-tight leading-none">
              BUILDINTEL <span className="text-[#FF9500]">AI</span>
            </div>
            <div className="text-xs text-white/50 mt-1">Set a new password</div>
          </div>
        </div>

        <div className="bi-surface p-6 sm:p-8">
          <h1 className="font-heading font-bold text-2xl">Choose a new password</h1>
          <p className="text-white/60 text-sm mt-1">
            At least 8 characters, with one number.
          </p>

          {!token && (
            <div
              data-testid="reset-missing-token"
              className="mt-5 rounded-lg border border-[#F87171]/40 bg-[#F87171]/10 px-3 py-2 text-[13px] text-[#FCA5A5]"
            >
              This reset link is missing a token. Request a new one from the
              forgot-password page.
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60">New password</label>
              <input
                data-testid="reset-password"
                type="password"
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bi-input"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60">Confirm password</label>
              <input
                data-testid="reset-confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full bi-input"
                placeholder="Type it again"
              />
              {passwordProblem && (
                <p
                  data-testid="reset-strength-hint"
                  className="mt-1.5 text-[11px] text-[#FCA5A5]"
                >
                  {passwordProblem}
                </p>
              )}
            </div>

            {error && (
              <div
                data-testid="reset-error"
                className="rounded-lg border border-[#F87171]/40 bg-[#F87171]/10 px-3 py-2 text-[13px] text-[#FCA5A5]"
              >
                {error}
              </div>
            )}

            <button
              data-testid="reset-submit"
              type="submit"
              disabled={!canSubmit}
              className="w-full h-14 rounded-xl bi-accent-btn text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Updating…</>
              ) : (
                <><ShieldCheck className="w-5 h-5" /> Reset password</>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link
              to="/login"
              data-testid="reset-back-to-login"
              className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-[#FF9500]"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
            <div>
              <Link
                to="/forgot-password"
                data-testid="reset-request-new"
                className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/70"
              >
                <KeyRound className="w-3.5 h-3.5" /> Request a new reset link
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
